import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Home,
  User,
  MapPin,
  Play,
  RefreshCw,
  HelpCircle,
  Pencil,
  Clock,
  ArrowRight,
  Download,
} from 'lucide-react';
import { motion } from 'framer-motion';

const MatchBadge = ({ status }) => {
  const styles = {
    exact: 'bg-green-500/20 text-green-400 border-green-500/30',
    partial: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    no_match: 'bg-red-500/20 text-red-400 border-red-500/30',
    lookup_failed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  const labels = {
    exact: 'Exact Match',
    partial: 'Partial Match',
    no_match: 'No Match',
    lookup_failed: 'Lookup Failed',
    pending: 'Pending',
  };

  const icons = {
    exact: <CheckCircle className="w-3 h-3" />,
    partial: <AlertTriangle className="w-3 h-3" />,
    no_match: <XCircle className="w-3 h-3" />,
    lookup_failed: <HelpCircle className="w-3 h-3" />,
    pending: <RefreshCw className="w-3 h-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
      {icons[status] || icons.pending}
      {labels[status] || status}
    </span>
  );
};

export default function OwnerVerificationPage() {
  const [pendingEmployees, setPendingEmployees] = useState([]);
  const [reviewEmployees, setReviewEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [decision, setDecision] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [massDeciding, setMassDeciding] = useState(false);
  const [interruptedJob, setInterruptedJob] = useState(null);
  const [resuming, setResuming] = useState(false);
  const [editForm, setEditForm] = useState({
    address: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let interval;
    if (jobId && jobStatus?.status === 'running') {
      interval = setInterval(checkJobStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [jobId, jobStatus?.status]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Get all pending employees (homeowner_status = pending)
      const pendingResponse = await api.get('/employees', { params: { homeowner_status: 'pending' } });
      setPendingEmployees(pendingResponse.data);

      // Get employees needing review
      const reviewResponse = await api.get('/employees/pending-review');
      setReviewEmployees(reviewResponse.data);

      // Check for a running or interrupted verification job
      const activeResponse = await api.get('/employees/verify-owners/active');
      const activeJob = activeResponse.data;
      if (activeJob.status === 'running') {
        setJobId(activeJob.id);
        setJobStatus(activeJob);
        setVerifying(true);
        setInterruptedJob(null);
      } else if (activeJob.status === 'interrupted') {
        setInterruptedJob(activeJob);
      } else {
        setInterruptedJob(null);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const startVerification = async (unverifiedOnly = false) => {
    try {
      setVerifying(true);
      const targets = unverifiedOnly
        ? pendingEmployees.filter(e => !e.owner_match_status)
        : pendingEmployees;
      const response = await api.post('/employees/verify-owners', { employee_ids: targets.map(e => e.id) });
      setJobId(response.data.job_id);
      setJobStatus({ status: 'running', total: response.data.total, completed: 0 });
      setInterruptedJob(null);
      toast.success(`Started verification for ${response.data.total} employees`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start verification');
      setVerifying(false);
    }
  };

  const resumeVerification = async () => {
    if (!interruptedJob) return;
    setResuming(true);
    try {
      const response = await api.post(`/employees/verify-owners/${interruptedJob.id}/resume`);
      setJobId(interruptedJob.id);
      setJobStatus({ ...interruptedJob, status: 'running' });
      setVerifying(true);
      setInterruptedJob(null);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume verification');
    } finally {
      setResuming(false);
    }
  };

  const checkJobStatus = async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`/employees/verify-owners/status/${jobId}`);
      setJobStatus(response.data);
      
      if (response.data.status === 'completed') {
        setVerifying(false);
        toast.success('Verification completed!');
        loadData(); // Refresh data
      } else if (response.data.status === 'failed') {
        setVerifying(false);
        toast.error('Verification failed: ' + response.data.error);
      } else if (response.data.status === 'interrupted') {
        setVerifying(false);
        setInterruptedJob(response.data);
        toast.warning('Verification was interrupted. You can resume it.');
      }
    } catch (error) {
      console.error('Failed to check status:', error);
    }
  };

  const openDecisionDialog = (employee) => {
    setSelectedEmployee(employee);
    setDecision('');
    setDialogOpen(true);
  };

  const openEditDialog = (employee) => {
    setSelectedEmployee(employee);
    setEditForm({
      address: employee.address || '',
      address_2: employee.address_2 || '',
      city: employee.city || '',
      state: employee.state || '',
      zip_code: employee.zip_code || '',
    });
    setEditDialogOpen(true);
  };

  const submitEdit = async () => {
    if (!selectedEmployee) return;
    
    try {
      await api.put(`/employees/${selectedEmployee.id}`, editForm);
      toast.success('Address updated');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update address');
    }
  };

  const reverifyEmployee = async (employee) => {
    try {
      // Clear the previous verification status
      await api.put(`/employees/${employee.id}`, {
        property_owner_name: null,
        owner_match_status: null,
      });
      
      // Start verification for just this employee
      const response = await api.post('/employees/verify-owners', { employee_ids: [employee.id] });
      toast.success('Re-verification started');
      
      // Poll for completion
      const checkInterval = setInterval(async () => {
        const statusRes = await api.get(`/employees/verify-owners/status/${response.data.job_id}`);
        if (statusRes.data.status === 'completed') {
          clearInterval(checkInterval);
          loadData();
          toast.success('Re-verification complete');
        }
      }, 2000);
      
    } catch (error) {
      toast.error('Failed to re-verify');
    }
  };

  const submitDecision = async () => {
    if (!selectedEmployee || !decision) return;
    
    try {
      await api.post(`/employees/${selectedEmployee.id}/owner-decision`, { 
        employee_id: selectedEmployee.id,
        decision 
      });
      toast.success(`Marked as ${decision}`);
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === reviewEmployees.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reviewEmployees.map(e => e.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleMassDecision = async (decisionType) => {
    if (selectedIds.size === 0) {
      toast.error('No employees selected');
      return;
    }
    
    setMassDeciding(true);
    try {
      const response = await api.post('/employees/mass-decision', {
        employee_ids: Array.from(selectedIds),
        decision: decisionType
      });
      toast.success(response.data.message);
      setSelectedIds(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setMassDeciding(false);
    }
  };

  const formatTenure = (days) => {
    if (!days) return '-';
    const years = (days / 365).toFixed(2);
    return `${years} yrs`;
  };

  const progress = jobStatus ? (jobStatus.completed / jobStatus.total) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="owner-verification-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Owner Verification</h1>
          <p className="text-muted-foreground mt-1">
            Verify homeowner status via RPR property lookup
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const response = await api.get('/export/verification-report', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `verification_report_${new Date().toISOString().split('T')[0]}.xlsx`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Report downloaded');
              } catch {
                toast.error('Failed to export report');
              }
            }}
            data-testid="export-verification-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Results
          </Button>
          <Button
            onClick={() => startVerification(true)}
            disabled={verifying || pendingEmployees.filter(e => !e.owner_match_status).length === 0}
            variant="outline"
            className="border-[#E31837]/40 text-[#E31837] hover:bg-[#E31837]/10"
            data-testid="verify-unverified-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Verify Not Looked Up ({pendingEmployees.filter(e => !e.owner_match_status).length})
          </Button>
          <Button
            onClick={() => startVerification(false)}
            disabled={verifying || pendingEmployees.length === 0}
            className="bg-[#E31837] hover:bg-[#c41530] text-white"
            data-testid="start-verification-btn"
          >
            {verifying ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Verify All Pending ({pendingEmployees.length})
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Interrupted Job Banner */}
      {interruptedJob && !verifying && (
        <Card className="bg-yellow-500/5 border-yellow-500/30" data-testid="interrupted-job-banner">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Verification was interrupted</p>
                  <p className="text-sm text-muted-foreground">
                    {interruptedJob.completed} of {interruptedJob.total} employees were looked up before the job stopped
                    {interruptedJob.error ? ` (${interruptedJob.error})` : ''}. {interruptedJob.remaining} remaining.
                  </p>
                </div>
              </div>
              <Button
                onClick={resumeVerification}
                disabled={resuming}
                className="bg-yellow-500 hover:bg-yellow-600 text-white shrink-0"
                data-testid="resume-verification-btn"
              >
                {resuming ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Resume ({interruptedJob.remaining} left)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Bar */}
      {verifying && jobStatus && (
        <Card className="bg-card border-border">
          <CardContent className="py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Verifying property owners...</span>
                <span>{jobStatus.completed} / {jobStatus.total}</span>
              </div>
              <Progress value={progress} className="h-2" />
              {jobStatus.results && jobStatus.results.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Last: {jobStatus.results[jobStatus.results.length - 1]?.employee_name} - 
                  {jobStatus.results[jobStatus.results.length - 1]?.match_status}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingEmployees.length}</p>
                <p className="text-sm text-muted-foreground">Pending Verification</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Search className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{reviewEmployees.length}</p>
                <p className="text-sm text-muted-foreground">Need Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <HelpCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="never-looked-up-count">
                  {pendingEmployees.filter(e => !e.owner_match_status).length}
                </p>
                <p className="text-sm text-muted-foreground">Never Looked Up</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <User className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {reviewEmployees.filter(e => e.owner_match_status === 'partial').length}
                </p>
                <p className="text-sm text-muted-foreground">Partial Match</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employees Needing Review */}
      {reviewEmployees.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Employees Needing Review</CardTitle>
              <CardDescription>
                Review property owner matches and make decisions
              </CardDescription>
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size} selected
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMassDecision('homeowner')}
                  disabled={massDeciding}
                  className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                >
                  <Home className="w-4 h-4 mr-1" />
                  Mark Homeowners
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMassDecision('renter')}
                  disabled={massDeciding}
                  className="text-blue-500 border-blue-500/30 hover:bg-blue-500/10"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Mark Renters
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-10">
                      <Checkbox
                        checked={selectedIds.size === reviewEmployees.length && reviewEmployees.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th>Employee</th>
                    <th>Tenure</th>
                    <th>Property Owner</th>
                    <th>Match</th>
                    <th>Address</th>
                    <th className="w-32">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewEmployees.map((employee) => (
                    <motion.tr
                      key={employee.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      data-testid={`review-row-${employee.id}`}
                      className={selectedIds.has(employee.id) ? 'bg-muted/30' : ''}
                    >
                      <td>
                        <Checkbox
                          checked={selectedIds.has(employee.id)}
                          onCheckedChange={() => toggleSelect(employee.id)}
                        />
                      </td>
                      <td>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {employee.first_name} {employee.last_name}
                          </span>
                          {employee.previous_homeowner_status && (
                            <span className="text-xs text-muted-foreground">
                              Was: <span className={employee.previous_homeowner_status === 'homeowner' ? 'text-green-400' : 'text-blue-400'}>{employee.previous_homeowner_status}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatTenure(employee.tenure_days)}
                        </div>
                      </td>
                      <td>
                        <span className={employee.property_owner_name ? 'text-sm' : 'text-muted-foreground italic text-sm'}>
                          {employee.property_owner_name?.replace(/\n/g, ' ') || 'Not found'}
                        </span>
                      </td>
                      <td>
                        <MatchBadge status={employee.owner_match_status} />
                      </td>
                      <td>
                        <div className="flex flex-col text-xs">
                          {employee.previous_address && (
                            <div className="flex items-center gap-1 text-muted-foreground line-through">
                              <span className="truncate max-w-[180px]">{employee.previous_address}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-muted-foreground" />
                            <span className={`truncate max-w-[180px] ${employee.previous_address ? 'text-green-400' : ''}`}>
                              {employee.address}, {employee.city}, {employee.state}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDecisionDialog(employee)}
                            data-testid={`decide-btn-${employee.id}`}
                          >
                            Decide
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(employee)}
                            title="Edit Address"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Employees */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle>Pending Verification</CardTitle>
          <CardDescription>
            Employees awaiting property owner lookup
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : pendingEmployees.length === 0 ? (
            <div className="empty-state py-12">
              <CheckCircle className="w-12 h-12 mb-4 text-green-500/50" />
              <h3 className="text-lg font-medium mb-2">All caught up!</h3>
              <p className="text-sm text-muted-foreground">
                No employees pending verification
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Property Owner</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingEmployees.slice(0, 20).map((employee) => (
                    <tr key={employee.id}>
                      <td className="font-medium">
                        {employee.first_name} {employee.last_name}
                      </td>
                      <td className="text-muted-foreground">
                        {employee.address}, {employee.city}, {employee.state} {employee.zip_code}
                      </td>
                      <td>
                        {employee.property_owner_name ? (
                          <span className="text-green-400">{employee.property_owner_name}</span>
                        ) : (
                          <span className="text-muted-foreground italic">Not looked up</span>
                        )}
                      </td>
                      <td>
                        {employee.owner_match_status ? (
                          <MatchBadge status={employee.owner_match_status} />
                        ) : (
                          <span className="status-pending px-2 py-1 rounded text-xs">
                            Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pendingEmployees.length > 20 && (
                <p className="text-center text-sm text-muted-foreground py-3">
                  And {pendingEmployees.length - 20} more...
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Decision Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle>Make Decision</DialogTitle>
            <DialogDescription>
              Review the owner information and decide on homeowner status
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Employee Name</p>
                  <p className="font-medium">{selectedEmployee.first_name} {selectedEmployee.last_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Property Owner</p>
                  <p className="font-medium">{selectedEmployee.property_owner_name || 'Not found'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Address</p>
                  <p>{selectedEmployee.address}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedEmployee.city}, {selectedEmployee.state} {selectedEmployee.zip_code}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-1">Match Status</p>
                  <MatchBadge status={selectedEmployee.owner_match_status} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Your Decision</label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger className="bg-background border-input">
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="homeowner">
                      <span className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-green-500" />
                        Homeowner
                      </span>
                    </SelectItem>
                    <SelectItem value="renter">
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-blue-500" />
                        Renter
                      </span>
                    </SelectItem>
                    <SelectItem value="pending">
                      <span className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        Keep Pending
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitDecision}
              disabled={!decision}
              className="bg-[#E31837] hover:bg-[#c41530] text-white"
            >
              Confirm Decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Address</DialogTitle>
            <DialogDescription>
              Update the address and re-verify with RPR
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium">{selectedEmployee.first_name} {selectedEmployee.last_name}</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={editForm.address}
                    onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                    placeholder="123 Main St"
                    className="bg-background"
                  />
                </div>
                <div>
                  <Label htmlFor="address_2">Apt/Suite (optional)</Label>
                  <Input
                    id="address_2"
                    value={editForm.address_2}
                    onChange={(e) => setEditForm({...editForm, address_2: e.target.value})}
                    placeholder="Apt 101"
                    className="bg-background"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={editForm.city}
                      onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                      className="bg-background"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={editForm.state}
                      onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                      className="bg-background"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      value={editForm.zip_code}
                      onChange={(e) => setEditForm({...editForm, zip_code: e.target.value})}
                      className="bg-background"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                await submitEdit();
                if (selectedEmployee) {
                  reverifyEmployee(selectedEmployee);
                }
              }}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Save & Re-verify
            </Button>
            <Button
              onClick={submitEdit}
              className="bg-[#E31837] hover:bg-[#c41530] text-white"
            >
              Save Only
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
